import {
  type BaseAndTargetElementsPayload,
  baseFreeFormatTypes,
  type BaseMetamodelMetadata,
  type DiagramPayload,
  type DraftData,
  type GenericObject,
  type PayloadDraftEntry,
  SupportedMetamodels,
} from '@bizzdesign/modeler/models';
import {
  isBasicData,
  isGraphicData,
  isTextType,
} from '@bizzdesign/modeler/utils-canvazz';
import { isUuid } from './formatting.utils';

export function payloadContainsDiagram(payload: GenericObject): boolean {
  // The assumption here is that a diagram payload entry always has an object ID as key
  if (payload != null) {
    for (const key of Object.keys(payload)) {
      if (isUuid(key)) {
        return true;
      }
    }
  }
  return false;
}

export function getEmptyDiagramPayload(): DiagramPayload {
  return {
    payload: null,
    fromRepoElements: [],
    stateToken: '',
  };
}

export function getPayloadBasedOnElementKind(
  payload: PayloadDraftEntry,
  elementKind: 'diagram' | 'model',
): PayloadDraftEntry {
  return Object.fromEntries(
    Object.entries<DraftData>(payload).filter(
      ([_, value]) => value.elementKind === elementKind,
    ),
  );
}

export function populateGraphicNameInPayload(
  graphicPayload: PayloadDraftEntry,
  semanticPayload: PayloadDraftEntry,
): PayloadDraftEntry {
  const graphics = Object.values(graphicPayload).filter(isGraphicData);
  const result = graphics.reduce((acc, graphic) => {
    if (!graphic.semanticsId) return acc;
    const semanticName = semanticPayload[graphic.semanticsId]?.name;
    acc[graphic.id] = {
      ...graphic,
      name: semanticName,
    };
    return acc;
  }, {} as PayloadDraftEntry);
  return result;
}

export function splitPayloadIntoBaseAndTargetElements(
  payload: PayloadDraftEntry,
): BaseAndTargetElementsPayload {
  const payloadEntries = Object.entries<DraftData>(payload);
  return payloadEntries.reduce<BaseAndTargetElementsPayload>(
    (acc, [key, value]) => {
      if (baseFreeFormatTypes.includes(value.typeName)) {
        acc.baseElements[key] = value;
      } else {
        acc.targetElements[key] = value;
      }
      return acc;
    },
    { baseElements: {}, targetElements: {} },
  );
}

/**
 * Filters out payload data so we only include models/semantics that have a corresponding graphic element.
 * @param payload draft payload
 * @param graphicContent an array of all graphical elements present in payload
 * @returns new updated payload
 */
export function getSemanticsThatHaveGraphicContent(
  payload: PayloadDraftEntry,
  graphicContent: DraftData[],
): PayloadDraftEntry {
  const semanticsEntries = Object.entries<DraftData>(payload).filter(
    ([_, value]) => value.elementKind === 'model',
  );

  const graphicContentArray = graphicContent.filter(isGraphicData);

  const updatedSemantics = semanticsEntries.filter(([_, modelElement]) =>
    graphicContentArray.find(
      (graphic) => graphic.semanticsId === modelElement.id,
    ),
  );
  const viewSemanticContent = Object.fromEntries(updatedSemantics);
  return viewSemanticContent;
}

export const removeEmptyTextAndLinkedLinesFromPayload = (
  payload: PayloadDraftEntry | null,
  metamodelMetadata?: BaseMetamodelMetadata,
): PayloadDraftEntry | null => {
  if (!payload) return null;
  const isFreeFormat =
    metamodelMetadata?.currentMetamodelName === SupportedMetamodels.FREEFORMAT;
  const filterFunc = isFreeFormat ? isGraphicData : isBasicData;
  const splitPayload = Object.values(payload).filter(filterFunc);

  const textIdsToRemove: string[] = splitPayload
    .filter(
      (el) =>
        isTextType(el.typeName) &&
        (el.name?.en === '' || el.name === null || el.name === undefined),
    )
    .map((el) => el.id);
  const linkIdsToRemove: string[] = splitPayload
    .filter(
      (el) =>
        el.typeName === 'Line' &&
        ((el.sourceId !== undefined &&
          isUuid(el.sourceId) &&
          textIdsToRemove.includes(el.sourceId)) ||
          (el.targetId !== undefined &&
            isUuid(el.targetId) &&
            textIdsToRemove.includes(el.targetId))),
    )
    .map((el) => el.id);

  let idsToRemove = new Set([...textIdsToRemove, ...linkIdsToRemove]);
  let graphicIdsToRemove = [];
  // if MA is on, remove the graphics associated with the semantics as well
  if (!isFreeFormat) {
    graphicIdsToRemove = Object.values(payload)
      .filter(isGraphicData)
      .filter((el) => el.semanticsId && idsToRemove.has(el.semanticsId))
      .map((el) => el.id);
    idsToRemove = new Set([
      ...textIdsToRemove,
      ...linkIdsToRemove,
      ...graphicIdsToRemove,
    ]);
  }

  const filteredPayload = Object.fromEntries(
    Object.entries(payload).filter(([id]) => !idsToRemove.has(id)),
  );
  return filteredPayload;
};
